import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/notifications';
import { trackEvents } from './events';
import {
  currentPermission,
  disablePush,
  getPref,
  isWebPushConfigured,
  isWebPushSupported,
  setPref,
  syncToken,
  type PushPermission,
} from './webPushController';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/use-web-push';

/** Por que o toggle está desabilitado: navegador sem suporte ou build sem VAPID. */
export type WebPushUnavailableReason = 'unsupported' | 'unconfigured';

/** Estado e ação expostos ao card de notificações. */
export type UseWebPush = {
  loading: boolean;
  available: boolean;
  unavailableReason: WebPushUnavailableReason | null;
  permission: PushPermission;
  prefEnabled: boolean;
  /** Estado efetivo do toggle: preferência ligada E permissão concedida. */
  on: boolean;
  busy: boolean;
  setEnabled: (enabled: boolean) => Promise<void>;
};

/**
 * Estado + ações de web push para o card de notificações em /settings do
 * console super-admin. O registro de token "global" é responsabilidade do
 * `<WebPushBootstrap />`.
 *
 * Origem: sem issue — commit 7994b1111, 2026-05-12 (web push FCM do admin).
 *
 * Contrato: `setEnabled(true)` grava a preferência e chama `syncToken`
 * (pode abrir o prompt do navegador); `setEnabled(false)` desregistra e apaga o
 * token local. Toast de sucesso/erro; em erro, o estado volta ao que está
 * gravado. Não lança.
 *
 * Usado por: `features/settings/NotificationsCard.tsx`.
 *
 * Dicas: `on` exige preferência ligada E permissão `granted` — preferência
 * ligada com permissão negada aparece desligada, de propósito.
 */
export function useWebPush(): UseWebPush {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(false);
  const [permission, setPermission] = useState<PushPermission>(() => currentPermission());
  const [prefEnabled, setPrefEnabled] = useState<boolean>(() => getPref());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void isWebPushSupported().then((supported): void => {
      if (cancelled) return;
      setAvailable(supported);
      setPermission(currentPermission());
      setLoading(false);
    });
    return (): void => {
      cancelled = true;
    };
  }, []);

  const unavailableReason: WebPushUnavailableReason | null = available
    ? null
    : !isWebPushConfigured()
      ? 'unconfigured'
      : 'unsupported';

  const on = prefEnabled && permission === 'granted';

  const setEnabled = useCallback(
    async (enabled: boolean): Promise<void> => {
      setBusy(true);
      try {
        if (enabled) {
          setPref(true);
          setPrefEnabled(true);
          const res = await syncToken();
          setPermission(res.permission);
          if (res.permission === 'denied') {
            toast.error(t('admin.settings.notifications.blocked'));
          } else if (res.registered) {
            toast.success(t('admin.settings.notifications.enabled'));
          }
        } else {
          await disablePush();
          setPref(false);
          setPrefEnabled(false);
          setPermission(currentPermission());
          trackEvents.pushDisabled();
          toast.success(t('admin.settings.notifications.disabled'));
        }
      } catch (err) {
        console.error(`${TAG}: setEnabled(${String(enabled)}) falhou`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_web_push_failed', {
          site: 'setEnabled() falhou',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        toast.error(t('admin.settings.notifications.error'));
        setPrefEnabled(getPref());
        setPermission(currentPermission());
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  return {
    loading,
    available,
    unavailableReason,
    permission,
    prefEnabled,
    on,
    busy,
    setEnabled,
  };
}
